/**
 * GlobalTradeContext — comunicazione cross-component per trade pendenti.
 * Usato per la feature "Crea nota nel Diario" dal QuickTradeWidget:
 * il widget salva un trade pendente qui, e il Diary lo legge al mount/focus.
 */

import React, { createContext, useContext, useState, useCallback } from 'react';
import { TradeData } from '../utils/tradeMapping';

interface GlobalTradeContextValue {
  /** Trade pendente da aprire nel Diario (null se nessuno) */
  pendingDiaryTrade: TradeData | null;
  /** Imposta il trade pendente (chiamato dal QuickTradeWidget) */
  setPendingDiaryTrade: (trade: TradeData | null) => void;
  /** Consuma e resetta il trade pendente (chiamato dal Diary) */
  consumePendingDiaryTrade: () => TradeData | null;
}

const GlobalTradeContext = createContext<GlobalTradeContextValue | null>(null);

export const GlobalTradeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [pendingDiaryTrade, setPendingDiaryTradeState] = useState<TradeData | null>(null);

  const setPendingDiaryTrade = useCallback((trade: TradeData | null) => {
    setPendingDiaryTradeState(trade);
  }, []);

  const consumePendingDiaryTrade = useCallback((): TradeData | null => {
    const trade = pendingDiaryTrade;
    setPendingDiaryTradeState(null);
    return trade;
  }, [pendingDiaryTrade]);

  return (
    <GlobalTradeContext.Provider value={{ pendingDiaryTrade, setPendingDiaryTrade, consumePendingDiaryTrade }}>
      {children}
    </GlobalTradeContext.Provider>
  );
};

export function useGlobalTrade(): GlobalTradeContextValue {
  const ctx = useContext(GlobalTradeContext);
  if (!ctx) throw new Error('useGlobalTrade must be used within GlobalTradeProvider');
  return ctx;
}

export default GlobalTradeContext;
